import { useState, useEffect, useCallback, createContext, useContext, type ReactNode } from "react";
import { useAuth } from "../lib/auth-context";
import { api, type Lot, type Order, type Member, type SommelierFull, type Stats } from "../lib/api";
import { useApi, useMutation } from "../hooks/useApi";

/* ═══════════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════════ */
const T = {
  forest: "#1a3028", forestDeep: "#0f1d17", forestMid: "#214033", forestLt: "#2d5942",
  ivory: "#f4efe4", ivoryWarm: "#ece3ce", ivoryDeep: "#ddd2b8",
  linen: "#c2b49a", brass: "#987940", brassLt: "#b5913a", brassGlow: "#cba752",
  ink: "#100e0b", smoke: "#524a40", mist: "#8a7d6e",
  white: "#ffffff", danger: "#8b1c1c", success: "#1e4d2b",
};

/* ═══════════════════════════════════════════════════════
   NAV CONTEXT (for view routing)
═══════════════════════════════════════════════════════ */
type NavCtxType = {
  view: string;
  setView: (v: string) => void;
  selectedLot: Lot | null;
  setSelectedLot: (l: Lot | null) => void;
};
const NavCtx = createContext<NavCtxType | null>(null);
const useNav = () => useContext(NavCtx)!;

/* ═══════════════════════════════════════════════════════
   GLOBAL STYLES
═══════════════════════════════════════════════════════ */
function GlobalStyle() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;1,300;1,400&family=Cinzel:wght@400;500&family=Jost:wght@200;300;400&family=Libre+Baskerville:ital,wght@0,400;1,400&display=swap');
      *{box-sizing:border-box;margin:0;padding:0}
      body{font-family:'Jost',sans-serif;font-weight:300;background:${T.ivory};color:${T.ink};overflow-x:hidden}
      input,select,textarea,button{font-family:'Jost',sans-serif}
      ::-webkit-scrollbar{width:4px}
      ::-webkit-scrollbar-track{background:${T.ivoryWarm}}
      ::-webkit-scrollbar-thumb{background:${T.linen};border-radius:2px}
      .fade-in{animation:fadeIn .4s ease}
      @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
      @keyframes marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}
      @keyframes scalePulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.4);opacity:.7}}
    `}</style>
  );
}

/* ═══════════════════════════════════════════════════════
   SHARED UI
═══════════════════════════════════════════════════════ */
const Badge = ({ children, color = "forest" }: { children: ReactNode; color?: string }) => {
  const colors: Record<string, { bg: string; text: string }> = {
    forest:  { bg: T.forest,    text: T.ivory },
    brass:   { bg: T.brassLt,   text: T.forestDeep },
    mist:    { bg: T.ivoryDeep, text: T.smoke },
    danger:  { bg: "#f5c4b3",   text: "#8b1c1c" },
    success: { bg: "#c0dd97",   text: "#1e4d2b" },
    info:    { bg: "#b5d4f4",   text: "#0c447c" },
  };
  const c = colors[color] ?? colors.mist;
  return (
    <span style={{ display: "inline-block", fontSize: ".5rem", letterSpacing: ".2em", textTransform: "uppercase", background: c.bg, color: c.text, padding: "3px 10px", borderRadius: 2 }}>
      {children}
    </span>
  );
};

const StatusBadge = ({ status }: { status: string }) => {
  const map: Record<string, { l: string; c: string }> = {
    delivered:  { l: "Delivered",  c: "success" }, scheduled: { l: "Scheduled", c: "info" },
    roasting:   { l: "Roasting",   c: "brass" },   dispatched: { l: "Dispatched", c: "forest" },
    pending:    { l: "Pending",    c: "mist" },     cancelled: { l: "Cancelled",  c: "danger" },
    paused:     { l: "Paused",     c: "danger" },   active:    { l: "Active",     c: "success" },
    archived:   { l: "Archived",   c: "mist" },
  };
  const s = map[status] ?? { l: status, c: "mist" };
  return <Badge color={s.c}>{s.l}</Badge>;
};

const TierBadge = ({ tier }: { tier?: string | null }) => {
  if (!tier) return null;
  const c = tier === "vault" ? "forest" : tier === "reserve" ? "brass" : "mist";
  return <Badge color={c}>{tier}</Badge>;
};

type BtnVariant = "primary" | "secondary" | "ghost" | "danger" | "brass";
const Btn = ({ children, onClick, variant = "primary", small, style: s, disabled, type = "button" }: {
  children: ReactNode; onClick?: () => void; variant?: BtnVariant;
  small?: boolean; style?: React.CSSProperties; disabled?: boolean; type?: "button" | "submit";
}) => {
  const base: React.CSSProperties = {
    fontFamily: "'Jost',sans-serif", fontWeight: 400, letterSpacing: ".18em",
    textTransform: "uppercase", border: "none", cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? .6 : 1, transition: "all .2s", display: "inline-block", ...s,
  };
  const vs: Record<BtnVariant, React.CSSProperties> = {
    primary:   { ...base, background: T.forest,  color: T.ivory,      padding: small ? "8px 20px" : "12px 32px", fontSize: small ? ".56rem" : ".6rem" },
    secondary: { ...base, background: "transparent", color: T.forest,  padding: small ? "8px 20px" : "12px 32px", fontSize: small ? ".56rem" : ".6rem", border: `1px solid ${T.forestMid}` },
    ghost:     { ...base, background: "transparent", color: T.mist,    padding: small ? "6px 14px" : "10px 24px", fontSize: small ? ".54rem" : ".58rem", border: `1px solid ${T.ivoryDeep}` },
    danger:    { ...base, background: T.danger,  color: T.ivory,      padding: small ? "8px 20px" : "12px 32px", fontSize: small ? ".56rem" : ".6rem" },
    brass:     { ...base, background: T.brassLt, color: T.forestDeep, padding: small ? "8px 20px" : "12px 32px", fontSize: small ? ".56rem" : ".6rem" },
  };
  return <button type={type} style={vs[variant]} onClick={onClick} disabled={disabled}>{children}</button>;
};

const Input = ({ label, value, onChange, type = "text", placeholder, rows, required, options, style: s }: {
  label?: string; value: string | number; onChange: (v: string) => void;
  type?: string; placeholder?: string; rows?: number; required?: boolean;
  options?: { value: string; label: string }[]; style?: React.CSSProperties;
}) => (
  <div style={{ marginBottom: 16, ...s }}>
    {label && <label style={{ display: "block", fontSize: ".56rem", letterSpacing: ".2em", textTransform: "uppercase", color: T.mist, marginBottom: 8 }}>{label}{required && <span style={{ color: T.danger }}> *</span>}</label>}
    {type === "textarea"
      ? <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows ?? 3}
          style={{ width: "100%", padding: "10px 14px", border: `1px solid ${T.ivoryDeep}`, background: T.ivory, fontFamily: "'Libre Baskerville',serif", fontStyle: "italic", fontSize: ".82rem", color: T.smoke, resize: "vertical", lineHeight: 1.7 }} />
      : type === "select"
      ? <select value={value} onChange={e => onChange(e.target.value)}
          style={{ width: "100%", padding: "10px 14px", border: `1px solid ${T.ivoryDeep}`, background: T.ivory, fontSize: ".8rem", color: T.ink, appearance: "auto" }}>
          {options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      : <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          style={{ width: "100%", padding: "10px 14px", border: `1px solid ${T.ivoryDeep}`, background: T.ivory, fontSize: ".82rem", color: T.ink }} />
    }
  </div>
);

const Card = ({ children, style: s }: { children: ReactNode; style?: React.CSSProperties }) => (
  <div style={{ background: T.white, border: `1px solid ${T.ivoryDeep}`, padding: "28px 24px", ...s }} className="fade-in">{children}</div>
);

const MetricCard = ({ label, value, sub, accent }: { label: string; value: string | number; sub?: string; accent?: boolean }) => (
  <div style={{ background: accent ? T.forest : T.white, border: `1px solid ${accent ? T.forestMid : T.ivoryDeep}`, padding: "24px 20px" }}>
    <div style={{ fontSize: ".5rem", letterSpacing: ".28em", textTransform: "uppercase", color: accent ? T.brassLt : T.mist, marginBottom: 8 }}>{label}</div>
    <div style={{ fontFamily: "'Cormorant',serif", fontSize: "2.2rem", fontWeight: 300, color: accent ? T.ivory : T.forest, lineHeight: 1, marginBottom: 4 }}>{value}</div>
    {sub && <div style={{ fontSize: ".65rem", color: accent ? T.linen : T.mist }}>{sub}</div>}
  </div>
);

const Spinner = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0" }}>
    <div style={{ width: 24, height: 24, border: `2px solid ${T.ivoryDeep}`, borderTopColor: T.brass, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

const ErrorMsg = ({ msg }: { msg: string }) => (
  <div style={{ padding: "12px 16px", background: "#fdf2f0", border: `1px solid ${T.danger}`, color: T.danger, fontSize: ".78rem", marginBottom: 16 }}>{msg}</div>
);

const Table = ({ cols, rows, onRowClick }: {
  cols: { key: string; label: string; muted?: boolean; wrap?: boolean; render?: (v: any, row: any) => ReactNode }[];
  rows: any[]; onRowClick?: (row: any) => void;
}) => (
  <div style={{ overflowX: "auto" }}>
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".78rem" }}>
      <thead>
        <tr style={{ borderBottom: `1px solid ${T.ivoryDeep}` }}>
          {cols.map(c => <th key={c.key + c.label} style={{ padding: "10px 16px", textAlign: "left", fontSize: ".52rem", letterSpacing: ".2em", textTransform: "uppercase", color: T.mist, fontWeight: 400, whiteSpace: "nowrap" }}>{c.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} onClick={() => onRowClick?.(r)}
            style={{ borderBottom: `1px solid ${T.ivoryWarm}`, cursor: onRowClick ? "pointer" : "default", transition: "background .15s" }}
            onMouseEnter={e => (e.currentTarget.style.background = T.ivoryWarm)}
            onMouseLeave={e => (e.currentTarget.style.background = "transparent")}>
            {cols.map(c => <td key={c.key + c.label} style={{ padding: "12px 16px", color: c.muted ? T.mist : T.ink, whiteSpace: c.wrap ? "normal" : "nowrap" }}>{c.render ? c.render(r[c.key], r) : r[c.key]}</td>)}
          </tr>
        ))}
        {rows.length === 0 && (
          <tr><td colSpan={cols.length} style={{ padding: "40px", textAlign: "center", color: T.mist, fontStyle: "italic", fontFamily: "'Libre Baskerville',serif", fontSize: ".85rem" }}>No records found</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

const Modal = ({ title, children, onClose, width = 560 }: { title: string; children: ReactNode; onClose: () => void; width?: number }) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(15,29,23,.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 24 }}
    onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
    <div style={{ background: T.ivory, width: "100%", maxWidth: width, maxHeight: "90vh", overflowY: "auto", padding: "36px 32px", position: "relative" }} className="fade-in">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <h3 style={{ fontFamily: "'Cinzel',serif", fontSize: "1.1rem", letterSpacing: ".12em", color: T.forestDeep }}>{title}</h3>
        <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "1.4rem", color: T.mist, lineHeight: 1, padding: "0 4px", cursor: "pointer" }}>×</button>
      </div>
      {children}
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════
   NAVIGATION
═══════════════════════════════════════════════════════ */
function Nav() {
  const { user, logout } = useAuth();
  const { view, setView } = useNav();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const isHome = view === "home";
  const bg = isHome && !scrolled ? "transparent" : `rgba(244,239,228,0.96)`;
  const logoColor = isHome && !scrolled ? T.ivory : T.forestDeep;
  const linkColor = isHome && !scrolled ? "rgba(244,239,228,.65)" : T.smoke;

  return (
    <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 500, height: 72, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 64px", background: bg, borderBottom: isHome && !scrolled ? "none" : `1px solid rgba(152,121,64,.16)`, backdropFilter: "blur(20px)", transition: "all .3s" }}>
      <div onClick={() => setView("home")} style={{ fontFamily: "'Cinzel',serif", fontSize: ".88rem", letterSpacing: ".38em", textTransform: "uppercase", color: logoColor, cursor: "pointer", transition: "color .3s" }}>
        Provenance
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 28 }}>
        {["origins", "membership"].map(v => (
          <span key={v} onClick={() => setView(v)} style={{ fontSize: ".58rem", letterSpacing: ".2em", textTransform: "uppercase", color: linkColor, cursor: "pointer", transition: "color .2s" }}
            onMouseEnter={e => (e.currentTarget.style.color = T.brassGlow)}
            onMouseLeave={e => (e.currentTarget.style.color = linkColor)}>
            {v}
          </span>
        ))}
        {user?.role === "member" && (
          <span onClick={() => setView("member-dash")} style={{ fontSize: ".58rem", letterSpacing: ".2em", textTransform: "uppercase", color: linkColor, cursor: "pointer" }}
            onMouseEnter={e => (e.currentTarget.style.color = T.brassGlow)}
            onMouseLeave={e => (e.currentTarget.style.color = linkColor)}>
            My Account
          </span>
        )}
        {user?.role === "admin" && (
          <span onClick={() => setView("admin-dash")} style={{ fontSize: ".58rem", letterSpacing: ".2em", textTransform: "uppercase", color: T.brassGlow, cursor: "pointer" }}>
            Admin ↗
          </span>
        )}
        {user
          ? <Btn small variant="ghost" onClick={() => { logout(); setView("home"); }} style={{ color: isHome && !scrolled ? T.ivory : T.smoke, borderColor: isHome && !scrolled ? "rgba(244,239,228,.3)" : T.ivoryDeep }}>Sign out</Btn>
          : <Btn small variant="ghost" onClick={() => setView("login")} style={{ color: isHome && !scrolled ? T.ivory : T.smoke, borderColor: isHome && !scrolled ? "rgba(244,239,228,.3)" : T.ivoryDeep }}>Sign in</Btn>
        }
      </div>
    </nav>
  );
}

export { GlobalStyle, Badge, StatusBadge, TierBadge, Btn, Input, Card, MetricCard, Spinner, ErrorMsg, Table, Modal, Nav, useNav, NavCtx, T };
